import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, type RequestBody } from '../client';
import { queryKeys } from '../react-query.config';
import { toast } from '@/hooks/use-toast';
import { api } from '../axios';

export const useGetUserLeagues = () =>
  useQuery({
    queryKey: queryKeys.user.leagues(),
    queryFn: async () => {
      return apiClient.get('/api/leagues');
    },
  });

export const useGetLeagueById = (leagueId: string) =>
  useQuery({
    queryKey: queryKeys.league.detail(leagueId),
    queryFn: async () => {
      return apiClient.get('/api/leagues/{id}', {
        pathParams: { id: leagueId },
      });
    },
    enabled: !!leagueId,
  });

export const useGetLeagueUsers = (leagueId: string) =>
  useQuery({
    queryKey: queryKeys.league.users(leagueId),
    queryFn: async () => {
      return apiClient.get('/api/leagues/{id}/users', {
        pathParams: { id: leagueId },
      });
    },
    enabled: !!leagueId,
  });

export const useGetLeagueTournaments = (leagueId?: string) =>
  useQuery({
    queryKey: queryKeys.league.tournaments(leagueId!),
    queryFn: async () => {
      return apiClient.get('/api/leagues/{id}/tournaments', {
        pathParams: { id: leagueId! },
      });
    },
    enabled: !!leagueId,
  });

export const useGetLeagueTournamentGroups = (leagueId: string) =>
  useQuery({
    queryKey: [...queryKeys.league.detail(leagueId), 'tournament-groups'],
    queryFn: async () => {
      return apiClient.get('/api/leagues/{id}/tournaments/group/visible', {
        pathParams: { id: leagueId },
      });
    },
    enabled: !!leagueId,
  });

export const useGetLeagueTournamentLeaderboard = (
  leagueId: string | undefined,
  tournamentId: string | undefined,
  options?: { limit?: number; offset?: number; pollMs?: number }
) => {
  const { limit = 50, offset = 0, pollMs = 60_000 } = options ?? {};
  return useQuery({
    queryKey: [
      ...queryKeys.league.detail(leagueId ?? ''),
      'tournament-leaderboard',
      tournamentId,
      limit,
      offset,
    ],
    queryFn: async () => {
      return apiClient.get('/api/leagues/{id}/tournaments/{tournamentId}/leaderboard', {
        pathParams: { id: leagueId!, tournamentId: tournamentId! },
        params: { limit, offset },
      });
    },
    enabled: !!leagueId && !!tournamentId,
    refetchInterval: pollMs,
  });
};

export const useCreateLeague = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RequestBody<'/api/leagues', 'post'>) => {
      return apiClient.post('/api/leagues', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.leagues() });
    },
  });
};

export const useJoinLeague = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RequestBody<'/api/leagues/join', 'post'>) => {
      return apiClient.post('/api/leagues/join', data);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.leagues() });
      toast({
        title: 'League Joined',
        description: data.message,
      });
    },
  });
};

export const useLeaveLeague = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leagueId }: { leagueId: string }) => {
      return apiClient.post('/api/leagues/{id}/leave', undefined, {
        pathParams: { id: leagueId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.leagues() });
      toast({
        title: 'Left League',
        description: 'You have successfully left the league.',
      });
    },
    onError: (error: { message?: string }) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to leave league',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteLeague = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leagueId }: { leagueId: string }) => {
      return api.delete(`/api/leagues/${leagueId}`, {
        headers: { 'Content-Type': 'text/plain' },
        data: JSON.stringify({}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.leagues() });
      toast({
        title: 'League Deleted',
        description: 'The league has been deleted successfully.',
      });
    },
    onError: (error: { message?: string }) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete league',
        variant: 'destructive',
      });
    },
  });
};

export const useCheckLeagueName = (name: string) =>
  useQuery({
    queryKey: ['league-name-check', name],
    queryFn: async () => {
      return apiClient.get('/api/leagues/check-name', {
        params: { name },
      });
    },
    enabled: !!name && name.length > 2,
  });

export const useRemoveUser = ({ leagueId }: { leagueId: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      return api.delete(`/api/leagues/${leagueId}/users/${userId}`, {
        headers: { 'Content-Type': 'text/plain' },
        data: JSON.stringify({}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.user.leagues(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.league.users(leagueId),
      });
      toast({
        title: 'User Removed',
        description: 'The user has been removed from the league.',
      });
    },
    onError: (error: { message?: string }) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove user',
        variant: 'destructive',
      });
    },
  });
};
